import { createContext, useCallback, useContext } from 'react';
import type { DateFormat, TimeFormat } from '../ui/format';

/**
 * The preferences context and its hooks, apart from the provider that fills them (Phase 103).
 *
 * React Fast Refresh updates a module in place only when everything it exports is a component. A
 * provider module that also exports a hook is invalidated on every edit instead — and a reloaded
 * module means a **new** context object, while the tree still mounted holds the old one. What the
 * person sees is the whole application dropping into its error boundary.
 *
 * So this module has no component in it, and almost no reason to change.
 */

export type Theme = 'system' | 'light' | 'dark';

export interface TablePrefs {
  /** Column keys shown, in the table's own order; undefined means the table's default set. */
  columns?: string[];
  pageSize?: number;
}

export interface Preferences {
  railCollapsed: boolean;
  railTheme: string;
  /** Whether a menu group opens beside the rail or inside it. */
  railFlyout: boolean;
  /** How a date is written. The language decides the words; this decides the order. */
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  /** Light, dark, or whatever the device says. */
  theme: Theme;
  /** Whether a notification also pops up as a toast, or only lands on the bell. */
  toasts: boolean;
  /**
   * Whether an arriving message or notification makes a sound.
   *
   * On by default, because somebody working a queue is not looking at the tab — and off in one
   * click, because somebody in an open-plan office needs it to be.
   */
  sounds: boolean;
  /** How each table is arranged, by table key. */
  tables: Record<string, TablePrefs>;
}

export interface PreferencesValue {
  preferences: Preferences;
  set: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
  /** True while a change is on its way to the server. */
  saving: boolean;
}

export const PreferencesContext = createContext<PreferencesValue | undefined>(undefined);

export function usePreferences(): PreferencesValue {
  const context = useContext(PreferencesContext);

  if (!context) throw new Error('usePreferences must be used inside a PreferencesProvider');

  return context;
}

/**
 * A table's own arrangement: the visible columns and the page size, persisted per person.
 *
 * Reads through the shared preferences so a change made in one list is saved the same way as the
 * rail's colour, and comes back on the next login on any machine.
 */
export function useTablePrefs(tableKey: string | undefined): {
  prefs: TablePrefs;
  save: (next: TablePrefs) => void;
} {
  const { preferences, set } = usePreferences();
  const prefs = tableKey ? (preferences.tables[tableKey] ?? {}) : {};

  const save = useCallback(
    (next: TablePrefs) => {
      if (!tableKey) return;
      set('tables', { ...preferences.tables, [tableKey]: next });
    },
    [tableKey, preferences.tables, set],
  );

  return { prefs, save };
}
